import { Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import "./App.css";

import Navbar from "./components/Navbar/Navbar";
import ScrollToTop from "./components/utils/ScrollToTop";
import HashScroll from "./components/utils/HashScroll";

// Public pages
import HomePage from "./pages/HomePage";
import CommunityPage from "./pages/CommunityPage";
import ContactPage from "./pages/ContactPage";
import DonatePage from "./pages/DonatePage";
import ApplyPage from "./pages/ApplyPage";
import Google from "./pages/Google";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfUsePage from "./pages/TermsOfUsePage";
import RefundPolicyPage from "./pages/RefundPolicyPage";

// Member portal
import LoginPage from "./portal/LoginPage";
import Dashboard from "./portal/Dashboard";
import JourneyPage from "./portal/JourneyPage";
import MyRetreatPage from "./portal/MyRetreatPage";
import VerificationPage from "./portal/VerificationPage";
import PrescreeningPage from "./portal/PrescreeningPage";
import MedicalPage from "./portal/MedicalPage";
import DocumentsPage from "./portal/DocumentsPage";
import ResourcesPage from "./portal/ResourcesPage";
import CommunityPortalPage from "./portal/CommunityPortalPage";
import DonationsPage from "./portal/DonationsPage";
import ProfilePage from "./portal/ProfilePage";
import NotificationsPage from "./portal/NotificationsPage";
import CoursesPage from "./portal/CoursesPage";
import BoardPage from "./portal/BoardPage";
import BookPage from "./portal/BookPage";
import CommunityConnectPage from "./portal/CommunityConnectPage";
import SsoPage from "./portal/SsoPage";

// Admin portal
import AdminLogin from "./admin/AdminLogin";
import AdminDashboard from "./admin/AdminDashboard";
import AdminApplications from "./admin/AdminApplications";
import AdminSubscribers from "./admin/AdminSubscribers";
import AdminDonations from "./admin/AdminDonations";
import AdminSettings from "./admin/AdminSettings";
import AdminStories from "./admin/AdminStories";
import AdminNotifications from "./admin/AdminNotifications";
import AdminCourses from "./admin/AdminCourses";
import AdminBoard from "./admin/AdminBoard";
import AdminResources from "./admin/AdminResources";
import AdminRooms from "./admin/AdminRooms";
import AdminRoles from "./admin/AdminRoles";
import AdminSecurity from "./admin/AdminSecurity";
import AdminRetreatBlocks from "./admin/AdminRetreatBlocks";
import AdminWorkshops from "./admin/AdminWorkshops";
import AdminProducts from "./admin/AdminProducts";
import AdminOrders from "./admin/AdminOrders";
import AdminQueue from "./admin/AdminQueue";
import AdminCohorts from "./admin/AdminCohorts";
import AdminBookings from "./admin/AdminBookings";
import AdminRoomCalendar from "./admin/makua/AdminRoomCalendar";
import AdminReservations from "./admin/makua/AdminReservations";
import AdminDatedRetreats from "./admin/makua/AdminDatedRetreats";
import AdminAccommodation from "./admin/makua/AdminAccommodation";
import AdminKitchen from "./admin/makua/AdminKitchen";
import AdminBookingSettings from "./admin/makua/AdminBookingSettings";
import AdminCourseLibrary from "./admin/makua/AdminCourseLibrary";
import AdminSellers from "./admin/makua/AdminSellers";
import AdminActivities from "./admin/makua/AdminActivities";
import AdminGuests from "./admin/makua/AdminGuests";
import { useCurrentAdmin } from "./admin/permissions";

/** A renamed page's old address keeps working (bookmarks, links in emails), query string included. */
function Moved({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/* Makua runs retreats through Retreat Schedule (V2.1): its old Retreat
   Blocks address goes there. Forge keeps the Retreat Blocks page. */
function RetreatBlocksRoute() {
  const { admin, loading } = useCurrentAdmin();
  if (loading) return null;
  if (admin?.features?.reservations) return <Moved to="/admin/retreats" />;
  return <AdminRetreatBlocks />;
}

/**
 * An admin-only build.
 *
 * This repo holds three things: the Forge public site, the member portal
 * and the admin panel. The admin panel is deployed a second time, pointed
 * at the Makua backend — and without this, admin.makuaretreats.com would
 * also serve Forge's homepage, and its /apply and /donate pages would be
 * writing Forge applications and donations into Makua's database.
 *
 * Set REACT_APP_ADMIN_ONLY=true on that deployment and everything outside
 * /admin redirects to the sign-in screen. Unset — which is what forge.ngo
 * does — nothing changes.
 */
const ADMIN_ONLY = process.env.REACT_APP_ADMIN_ONLY === "true";

function AppContent() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isPortal = pathname.startsWith("/portal");
  const isAdmin = pathname.startsWith("/admin");
  // Hide navbar when the full-screen video is open via URL param
  const isFilmOpen = pathname === "/" && searchParams.has("forgeintro");

  return (
    <div className="app">
      <ScrollToTop />
      <HashScroll />
      {/* Only show public navbar on public pages */}
      {!ADMIN_ONLY && !isPortal && !isAdmin && !isFilmOpen && <Navbar />}
      <Routes>
        {/* The public site and the member portal exist only on the Forge
            deployment. On an admin-only host they are not routes at all,
            so /apply and /donate cannot write into the other product. */}
        {!ADMIN_ONLY && (
          <>
          {/* ── Public ── */}
          <Route path="/" element={<HomePage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/donate" element={<DonatePage />} />
          <Route path="/apply" element={<ApplyPage />} />
          <Route path="/google" element={<Google />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />

          {/* ── Member portal ── */}
          <Route path="/portal/login" element={<LoginPage />} />
          <Route path="/portal/dashboard" element={<Dashboard />} />
          <Route path="/portal/journey" element={<JourneyPage />} />
          <Route path="/portal/my-retreat" element={<MyRetreatPage />} />
          <Route path="/portal/verification" element={<VerificationPage />} />
          <Route path="/portal/prescreening" element={<PrescreeningPage />} />
          <Route path="/portal/medical" element={<MedicalPage />} />
          {/* old "apply for a retreat" page → the booking flow */}
          <Route path="/portal/apply" element={<Navigate to="/portal/book" replace />} />
          <Route path="/portal/book" element={<BookPage />} />
          <Route path="/portal/documents" element={<DocumentsPage />} />
          <Route path="/portal/resources" element={<ResourcesPage />} />
          <Route path="/portal/community" element={<CommunityPortalPage />} />
          <Route path="/portal/community/connect" element={<CommunityConnectPage />} />
          <Route path="/portal/sso" element={<SsoPage />} />
          <Route path="/portal/donations" element={<DonationsPage />} />
          <Route path="/portal/profile" element={<ProfilePage />} />
          <Route path="/portal/notifications" element={<NotificationsPage />} />
          <Route path="/portal/courses" element={<CoursesPage />} />
          <Route path="/portal/board" element={<BoardPage />} />

          {/* ── Admin portal ── */}
          </>
        )}

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/applications" element={<AdminApplications />} />
        <Route path="/admin/subscribers" element={<AdminSubscribers />} />
        <Route path="/admin/donations" element={<AdminDonations />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/stories" element={<AdminStories />} />
        <Route path="/admin/notifications" element={<AdminNotifications />} />
        <Route path="/admin/courses" element={<AdminCourses />} />
        <Route path="/admin/board" element={<AdminBoard />} />
        <Route path="/admin/resources" element={<AdminResources />} />
        <Route path="/admin/rooms" element={<AdminRooms />} />
        <Route path="/admin/staff" element={<AdminRoles />} />
        <Route path="/admin/roles" element={<Moved to="/admin/staff" />} />
        <Route path="/admin/security" element={<AdminSecurity />} />
        <Route path="/admin/retreat-blocks" element={<RetreatBlocksRoute />} />
        <Route path="/admin/workshops" element={<AdminWorkshops />} />
        <Route path="/admin/products" element={<AdminProducts />} />
        <Route path="/admin/orders" element={<AdminOrders />} />
        <Route path="/admin/queue" element={<AdminQueue />} />
        <Route path="/admin/cohorts" element={<AdminCohorts />} />
        <Route path="/admin/bookings" element={<AdminBookings />} />
        <Route path="/admin/calendar" element={<AdminRoomCalendar />} />
        <Route path="/admin/reservations" element={<AdminReservations />} />
        <Route path="/admin/retreats" element={<AdminDatedRetreats />} />
        <Route path="/admin/dated-retreats" element={<Moved to="/admin/retreats" />} />
        <Route path="/admin/activities" element={<AdminActivities />} />
        <Route path="/admin/guests" element={<AdminGuests />} />
        <Route path="/admin/accommodation" element={<AdminAccommodation />} />
        <Route path="/admin/kitchen" element={<AdminKitchen />} />
        <Route path="/admin/booking-settings" element={<AdminBookingSettings />} />
        <Route path="/admin/course-library" element={<AdminCourseLibrary />} />
        <Route path="/admin/sellers" element={<AdminSellers />} />

        {ADMIN_ONLY && <Route path="*" element={<Navigate to="/admin/login" replace />} />}
      </Routes>
    </div>
  );
}

export default function App() {
  return <AppContent />;
}
